"use client";

import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardFooter } from "@/components/ui/card";
import { Pin, MoreVertical, LayoutGrid, AlertCircle, X, CheckSquare, RotateCcw, Archive, Edit, UserPlus } from "lucide-react";
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TeamAvatars } from '@/components/TeamAvatars';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Compliance Preview Component - NOW USES PROPS INSTEAD OF API CALLS
// This eliminates ~5 API calls per card (was the main N+1 query problem)
const CompliancePreview = ({ complianceSummary, isDarkBackground }) => {
  // Use compliance data passed from parent (pre-computed server-side)
  const certData = {
    valid: complianceSummary?.valid || 0,
    expiring: complianceSummary?.expiring || 0,
    expired: complianceSummary?.expired || 0,
    missing: complianceSummary?.missing || 0,
    total: complianceSummary?.total_required || 0
  };

  const compliancePercentage = complianceSummary?.percentage || 0;

  return (
    <div className="space-y-1">
      {/* Certificate Status Cards */}
      <div className="grid grid-cols-4 gap-1">
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-green-500/20 border-green-400/30'
            : 'bg-green-50 border-green-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-green-200' : 'text-green-700'
          }`}>{certData.valid}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-green-300' : 'text-green-600'
          }`}>Valid</div>
        </div>
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-amber-500/20 border-amber-400/30'
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-amber-200' : 'text-amber-700'
          }`}>{certData.expiring}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-amber-300' : 'text-amber-600'
          }`}>Expiring</div>
        </div>
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-red-500/20 border-red-400/30'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-red-200' : 'text-red-700'
          }`}>{certData.expired}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-red-300' : 'text-red-600'
          }`}>Expired</div>
        </div>
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-white/20 border-white/30'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-gray-200' : 'text-gray-700'
          }`}>{certData.missing}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-gray-300' : 'text-gray-600'
          }`}>Missing</div>
        </div>
      </div>

      {/* Compliance Progress Bar */}
      <div className="space-y-0.5">
        <div className={`flex justify-between text-[8px] ${
          isDarkBackground ? 'text-gray-300' : 'text-muted-foreground'
        }`}>
          <span>Compliance Rate</span>
          <span className="font-medium">{compliancePercentage}%</span>
        </div>
        <div className={`h-1.5 rounded-full overflow-hidden ${
          isDarkBackground ? 'bg-white/20' : 'bg-gray-200'
        }`}>
          <div
            className={`h-full transition-all duration-500 ${
              compliancePercentage >= 80 ? 'bg-green-500' :
              compliancePercentage >= 50 ? 'bg-amber-500' :
              'bg-red-500'
            }`}
            style={{ width: `${compliancePercentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export function ProjectCard({ project, onColorChange, onPin, onRemoveFromStack, isDragActive, isDropTarget, extraMenuItems, stackId, stackName, isSelectionMode, isSelected, onSelectionToggle, onEnterSelectionMode, onRestore, showRestoreButton, onArchive, onEditDetails, onEditAccess }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  // Preset colors for quick selection
  const presetColors = [
    { name: 'White', value: '#FFFFFF' },
    { name: 'Yellow', value: '#FEF08A' },
    { name: 'Orange', value: '#FED7AA' },
    { name: 'Pink', value: '#FECACA' },
    { name: 'Rose', value: '#F9A8D4' },
    { name: 'Purple', value: '#DDD6FE' },
    { name: 'Blue', value: '#BFDBFE' },
    { name: 'Green', value: '#BBF7D0' },
    { name: 'Gray', value: '#E5E7EB' },
    { name: 'Slate', value: '#CBD5E1' },
  ];

  // Helper function to determine if a color is dark
  const isColorDark = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Using luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const isDarkBackground = isColorDark(project.color?.value);
  
  const {
    attributes,
    listeners,
    setNodeRef: setDragNodeRef,
    isDragging,
  } = useDraggable({ 
    id: project.id.toString(),
    data: { project }
  });

  const {
    setNodeRef: setDropNodeRef,
  } = useDroppable({ id: project.id.toString() });

  const setNodeRef = (node) => {
    setDragNodeRef(node);
    setDropNodeRef(node);
  };

  const style = {
    opacity: isDragging ? 0.5 : 1,
    transition: 'opacity 0.2s ease-in-out',
  };
  
  const handleColorChange = (colorValue) => {
    const colorObj = {
      name: 'custom',
      value: colorValue
    };
    console.log('Color change:', colorObj, 'for project:', project.id);
    onColorChange?.(project.id, colorObj);
  };

  const cardStyle = {
    ...style,
    backgroundColor: project.color?.value || 'white'
  };

  const handleClick = (e) => {
    // If in selection mode, toggle selection instead of navigating
    if (isSelectionMode) {
      e.stopPropagation();
      onSelectionToggle?.(project.id);
      return;
    }

    // Only navigate if we're not currently dragging
    if (!isDragging) {
      // Get click coordinates
      const rect = e.currentTarget.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;

      // Create expanding ripple effect
      const ripple = document.createElement('div');
      ripple.style.position = 'fixed';
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      ripple.style.width = '20px';
      ripple.style.height = '20px';
      ripple.style.borderRadius = '50%';
      ripple.style.background = 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)';
      ripple.style.transform = 'translate(-50%, -50%)';
      ripple.style.zIndex = '9999';
      ripple.style.pointerEvents = 'none';
      ripple.style.transition = 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1)';
      ripple.style.opacity = '1';

      document.body.appendChild(ripple);

      // Expand the ripple
      requestAnimationFrame(() => {
        const size = Math.max(window.innerWidth, window.innerHeight) * 2.5;
        ripple.style.width = `${size}px`;
        ripple.style.height = `${size}px`;
        ripple.style.opacity = '0.9';
      });

      // Navigate after a short delay, passing stack context if available
      setTimeout(() => {
        navigate(`/space/${project.id}`, {
          state: stackId ? { fromStack: stackId, stackName } : undefined
        });
      }, 200);

      // Fade out and clean up
      setTimeout(() => {
        ripple.style.opacity = '0';
        setTimeout(() => ripple.remove(), 300);
      }, 400);
    }
  };

  return (
    <Card 
      ref={setNodeRef} 
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={`pb-0 w-full h-[200px] flex flex-col relative group shadow-md hover:shadow-xl transition-shadow duration-500 ease-in-out cursor-pointer ${
        isDropTarget ? 'bg-blue-50 border-2 border-dashed border-blue-500' : ''
      } ${isDragActive ? 'pointer-events-none' : ''}`}
      style={{
        ...cardStyle
      }}
      onMouseEnter={() => !isDragActive && setIsHovered(true)}
      onMouseLeave={() => !isDragActive && setIsHovered(false)}
    >
      {/* Selection Checkbox - Visible in selection mode */}
      {isSelectionMode && (
        <div
          className="absolute top-2 left-2 z-20"
          onClick={(e) => {
            e.stopPropagation();
            onSelectionToggle?.(project.id);
          }}
        >
          <div
            className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
              isSelected
                ? 'bg-blue-600 border-blue-600'
                : 'bg-white border-gray-300 hover:border-blue-400'
            }`}
          >
            {isSelected && (
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Restore Button - Always visible when viewing archived spaces */}
      {showRestoreButton && onRestore && !isSelectionMode && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-[0.5px] flex items-center justify-center z-10 rounded-lg">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRestore(project.id);
            }}
            className="bg-green-600 hover:bg-green-700 text-white shadow-lg"
            size="lg"
          >
            <RotateCcw className="h-5 w-5 mr-2" />
            Restore Space
          </Button>
        </div>
      )}

      {/* 3-Dot Menu - Only visible on hover (hide in selection mode) */}
      <div
        className={`absolute top-2 right-2 z-10 transition-opacity duration-200 ${
          isSelectionMode ? 'opacity-0 pointer-events-none' : (isHovered || isDropdownOpen ? 'opacity-100' : 'opacity-0')
        }`}
        data-no-dnd="true"
      >
        <DropdownMenu modal={false} open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 w-8 p-0 ${isDarkBackground ? 'hover:bg-white/20' : 'hover:bg-black/10'}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              data-no-dnd="true"
            >
              <MoreVertical className={`h-4 w-4 ${isDarkBackground ? 'text-white' : 'text-gray-600'}`} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {onEnterSelectionMode && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onEnterSelectionMode?.(project.id);
                  setIsDropdownOpen(false);
                }}
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                Select
              </DropdownMenuItem>
            )}

            {onPin && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log('Pin clicked for project:', project.name, 'onPin exists:', !!onPin);
                  onPin?.(project);
                  setIsDropdownOpen(false);
                }}
              >
                <Pin className={`h-4 w-4 mr-2 ${project.isPinned ? 'fill-blue-600 text-blue-600' : ''}`} />
                {project.isPinned ? 'Unpin' : 'Pin'}
              </DropdownMenuItem>
            )}

            {/* Edit Details and Edit Access - only show for non-archived spaces */}
            {!showRestoreButton && onEditDetails && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onEditDetails?.(project);
                  setIsDropdownOpen(false);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Details
              </DropdownMenuItem>
            )}

            {!showRestoreButton && onEditAccess && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onEditAccess?.(project);
                  setIsDropdownOpen(false);
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Edit Access
              </DropdownMenuItem>
            )}

            {onRemoveFromStack && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemoveFromStack?.(project.id);
                  setIsDropdownOpen(false);
                }}
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <X className="h-4 w-4 mr-2" />
                Remove from Stack
              </DropdownMenuItem>
            )}

            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2">
                <div className="w-4 h-4 rounded border border-gray-300 mr-2" style={{ backgroundColor: project.color?.value || '#FFFFFF' }} />
                Colour
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48">
                <div className="p-2 space-y-2">
                  {/* Preset Colors Grid */}
                  <div className="grid grid-cols-5 gap-1">
                    {presetColors.map((color) => (
                      <button
                        key={color.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleColorChange(color.value);
                        }}
                        className={`w-7 h-7 rounded border-2 hover:border-gray-400 transition-colors ${
                          color.value === '#FFFFFF' ? 'border-gray-300' : 'border-gray-200'
                        } ${project.color?.value === color.value ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>

                  {/* Custom Color Picker */}
                  <div className="border-t pt-2">
                    <label className="text-xs text-gray-600 mb-1 block">Custom Colour</label>
                    <input
                      type="color"
                      value={project.color?.value || '#FFFFFF'}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleColorChange(e.target.value);
                      }}
                      className="w-full h-8 rounded cursor-pointer border border-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Show Archive OR Restore, not both - based on archive status */}
            {showRestoreButton ? (
              // In archive view - show Restore option
              onRestore && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRestore?.(project.id);
                    setIsDropdownOpen(false);
                  }}
                  className="text-green-600 focus:text-green-600 focus:bg-green-50"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore Space
                </DropdownMenuItem>
              )
            ) : (
              // In normal view - show Archive option
              onArchive && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onArchive?.(project.id);
                    setIsDropdownOpen(false);
                  }}
                  className="text-orange-600 focus:text-orange-600 focus:bg-orange-50"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archive Space
                </DropdownMenuItem>
              )
            )}

            {/* Extra menu items passed via props */}
            {extraMenuItems && extraMenuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <CardHeader className="flex-1 pb-2 min-w-0">
        <div className="relative flex items-start gap-3 pr-10 min-w-0">
          {/* Space Avatar */}
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage
              src={project.avatar}
              alt="Space avatar"
              className="object-cover"
            />
            <AvatarFallback>{project.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 overflow-hidden space-y-0.5">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex-1 min-w-0 overflow-hidden">
                <TooltipProvider delayDuration={1000}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="min-w-0 overflow-hidden">
                        <h3 className={`text-sm sm:text-base leading-5 sm:leading-6 font-semibold truncate ${
                          isDarkBackground ? 'text-white' : 'text-card-foreground'
                        }`}>
                          {project.name}
                        </h3>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-sm">{project.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {showRestoreButton && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-orange-200 text-xs px-2 py-0 h-5 flex-shrink-0">
                  <Archive className="h-3 w-3 mr-1" />
                  Archived
                </Badge>
              )}
            </div>
            <p className={`text-xs sm:text-sm truncate ${
              isDarkBackground ? 'text-gray-200' : 'text-muted-foreground'
            }`}>
              {project.description || project.type}
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Compliance Preview Section */}
      <div className="px-6 -mt-5 pb-0">
        <CompliancePreview complianceSummary={project.complianceSummary} isDarkBackground={isDarkBackground} />
      </div>

      <CardFooter className="mt-auto -mt-3 pb-4 px-4 relative flex items-center justify-between">
        {project.visibility_type === 'company_wide' ? (
          <Badge variant="secondary" className="px-1.5 py-0.5 text-xs">
            Company Wide
          </Badge>
        ) : (
          <TeamAvatars
            teamMembers={project.teamMembers}
            isDarkBackground={isDarkBackground}
            maxVisible={4}
            size="sm"
          />
        )}

        {/* Tool Count */}
        {project.toolCount !== undefined && project.toolCount > 0 && (
          <div className="flex items-center gap-1.5">
            <LayoutGrid className={`h-3.5 w-3.5 ${
              isDarkBackground ? 'text-gray-300' : 'text-gray-600'
            }`} />
            <span className={`text-xs font-medium ${
              isDarkBackground ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {project.toolCount} tool{project.toolCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}